import axios from 'axios';
const API_URL = process.env.VUE_APP_API_URL;

/**
 * Flatten source-scoped field mappings into { logType: [fields] }.
 * The API returns { source: { logType: [fields] } } — this merges fields
 * across all sources, deduplicating by field name (first source wins).
 */
export function flattenFieldMappings(sourceScoped) {
  if (!sourceScoped || typeof sourceScoped !== 'object') return {}
  const flat = {}
  for (const logTypeMap of Object.values(sourceScoped)) {
    if (!logTypeMap || typeof logTypeMap !== 'object') continue
    for (const [logType, fields] of Object.entries(logTypeMap)) {
      if (!Array.isArray(fields)) continue
      if (!flat[logType]) {
        flat[logType] = fields
      } else {
        // Merge: add fields not yet present
        const existing = new Set(flat[logType].map(f => typeof f === 'object' ? f.name : f))
        for (const field of fields) {
          const name = typeof field === 'object' ? field.name : field
          if (!existing.has(name)) {
            flat[logType].push(field)
            existing.add(name)
          }
        }
      }
    }
  }
  return flat
}

class SourcesService {
  async list() {
    try {
      const { data } = await axios.get(API_URL + '/sources');
      return data;
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) return { status: 'unauthorized', data: [] }
        if (error.response.status === 403) return { status: 'forbidden', data: [] }
      }
      return { status: 'failed', data: [] }
    }
  }

  async listLogTypes(sourceId) {
    const { data } = await axios.get(`${API_URL}/sources/${sourceId}/log-types`)
    return data
  }

  async deleteLogType(sourceId, logTypeId) {
    const { data } = await axios.delete(`${API_URL}/sources/${sourceId}/log-types/${logTypeId}`)
    return data
  }

  async deleteSource(sourceId) {
    const { data } = await axios.delete(`${API_URL}/sources/${sourceId}`)
    return data
  }

  async createSource(displayName) {
    const { data } = await axios.post(`${API_URL}/sources`, { display_name: displayName })
    return data
  }

  async createLogType(sourceId, payload) {
    const { data } = await axios.post(`${API_URL}/sources/${sourceId}/log-types`, payload)
    return data
  }

  async editSource(sourceId, method, payload) {
    const { data } = await axios.put(`${API_URL}/sources/${sourceId}`, { method, data: payload })
    return data
  }

  async createGroup(displayName) {
    const { data } = await axios.post(`${API_URL}/sources/groups`, { display_name: displayName })
    return data
  }

  async updateGroup(groupId, payload) {
    const { data } = await axios.put(`${API_URL}/sources/groups/${groupId}`, payload)
    return data
  }

  async deleteGroup(groupId) {
    const { data } = await axios.delete(`${API_URL}/sources/groups/${groupId}`)
    return data
  }

  async getSourceStats(sourceId) {
    const { data } = await axios.get(`${API_URL}/sources/${sourceId}/stats`)
    return data
  }

  async updateAnnotations(sourceId, logTypeId, annotations) {
    const { data } = await axios.put(`${API_URL}/sources/${sourceId}/log-types/${logTypeId}/annotations`, { annotations })
    return data
  }

  async listIngestJobs(sourceId, typeName, { status, limit, offset } = {}) {
    const params = { type: typeName, limit: limit || 20, offset: offset || 0 }
    if (status !== undefined && status !== null) params.status = status
    const { data } = await axios.get(`${API_URL}/sources/${sourceId}/ingest-jobs`, { params })
    return data
  }

  async getSchema(sources = []) {
    try {
      const params = {};
      if (sources.length > 0) params.sources = sources;
      const { data } = await axios.get(API_URL + '/sources/schema', { params });
      return data;
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) return { status: 'unauthorized', data: {} }
        if (error.response.status === 403) return { status: 'forbidden', data: {} }
      }
      return { status: 'failed', data: {} }
    }
  }
}

export default new SourcesService();
